import React from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { withTranslation } from 'react-i18next';

/**
 * The financing status card on the invoice view, shared by Svea and Resurs.
 *
 * ``details`` is a list of { label, value } rows; ``linkUrl`` shows the customer's link with
 * a copy button; ``attention`` is a warning (financing approved but not booked on this
 * invoice); ``actions`` are buttons rendered under the rows.
 */
const FinancingPanel = ({ t, title, badge, subtitle, details = [], linkUrl, onCopied, attention, actions, children, testId }) => {
  const copyLink = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(linkUrl);
    if (onCopied) onCopied();
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }} data-testid={testId}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {badge}
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      {attention && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid={testId ? `${testId}-attention` : undefined}>
          {attention}
        </Alert>
      )}
      {details.filter((row) => row && row.value).length > 0 && (
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 3 }, mt: 1, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
          {details.filter((row) => row && row.value).map((row) => (
            <Typography key={row.label} variant="body2">
              {row.label}: <strong>{row.value}</strong>
            </Typography>
          ))}
        </Box>
      )}
      {children}
      {linkUrl && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all', flex: 1, minWidth: 0 }}>
            {linkUrl}
          </Typography>
          <Button size="small" variant="outlined" onClick={copyLink}>
            {t('financing.copyLink')}
          </Button>
        </Box>
      )}
      {actions && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          {actions}
        </Box>
      )}
    </Paper>
  );
};

export default withTranslation()(FinancingPanel);
